import React, { useEffect, useMemo } from 'react'
import { Check } from '@phosphor-icons/react'
import type { ContactContextField } from '@/types/workflow'
import { useSettingsStore } from '../../../../stores/settingsStore'
import { labelClass, labelStyle, sectionEyebrowClass, sectionEyebrowStyle } from './_kit'
import { ENRICHMENT_SELECTOR_GROUPS } from '../../enrichmentCatalog'

interface ContactFieldSelectorProps {
  selectedFields: ContactContextField[]
  onChange: (fields: ContactContextField[]) => void
  upstreamEnrichmentFields?: ContactContextField[]
}

interface FieldGroup {
  name: string
  /** Enrichment-derived groups only offer fields an upstream Enrich Contacts
   *  node actually produces; base groups come from the contact record itself. */
  enrichment?: boolean
  fields: { key: ContactContextField; label: string }[]
}

// Base groups come from the contact record itself (always available). The
// enrichment groups between them are derived from the shared enrichment catalog
// (enrichmentCatalog.ts) so this list can never drift from what an upstream
// Enrich Contacts node actually produces (see AB#21980). Custom AI is handled
// separately below via getCustomAiGroups() because its groups are recipe-driven.
const baseGroupsBefore: FieldGroup[] = [
  {
    name: 'Professional',
    fields: [
      { key: 'job_title', label: 'Job Title' },
      { key: 'department', label: 'Department' },
      { key: 'seniority_level', label: 'Seniority Level' },
    ],
  },
  {
    name: 'Company',
    fields: [
      { key: 'company_name', label: 'Company Name' },
      { key: 'company_website', label: 'Website' },
      { key: 'company_industry', label: 'Industry' },
      { key: 'company_size', label: 'Company Size' },
      { key: 'company_revenue', label: 'Revenue' },
    ],
  },
  {
    name: 'Location',
    fields: [
      { key: 'city', label: 'City' },
      { key: 'state', label: 'State' },
      { key: 'country', label: 'Country' },
    ],
  },
]

const enrichmentGroups: FieldGroup[] = ENRICHMENT_SELECTOR_GROUPS.map(g => ({
  name: g.name,
  enrichment: true,
  fields: g.fields.map(f => ({ key: f.key, label: f.label })),
}))

const engagementGroup: FieldGroup = {
  name: 'Engagement',
  fields: [
    { key: 'lead_score', label: 'Lead Score' },
    { key: 'lead_status', label: 'Lead Status' },
    { key: 'tags', label: 'Tags' },
    { key: 'segments', label: 'Segments' },
  ],
}

const fieldGroups: FieldGroup[] = [...baseGroupsBefore, ...enrichmentGroups, engagementGroup]

function getCustomAiGroups(): FieldGroup[] {
  const { recipes } = useSettingsStore.getState()
  if (recipes.length === 0) return []

  return recipes.map(recipe => {
    const label = recipe.output_mode === 'fields' && Array.isArray(recipe.output_schema)
      ? `${recipe.name} (${recipe.output_schema.map((f: { name: string }) => f.name).join(', ')})`
      : `${recipe.name} — Custom AI Result`

    return {
      name: `Custom AI: ${recipe.name}`,
      enrichment: true,
      fields: [
        { key: 'custom_ai_enrichment' as ContactContextField, label },
      ],
    }
  })
}

const ContactFieldSelector: React.FC<ContactFieldSelectorProps> = ({ selectedFields = [], onChange, upstreamEnrichmentFields = [] }) => {
  const { fetchRecipes } = useSettingsStore()

  useEffect(() => {
    fetchRecipes().catch(() => {})
  }, [fetchRecipes])

  const customAiGroups = getCustomAiGroups()

  // Only offer fields that will actually have data at this node: base groups
  // always (they come from the contact record), enrichment groups only for
  // fields an upstream Enrich Contacts node produces. Empty groups disappear.
  const visibleGroups = useMemo(() => {
    const all = [...fieldGroups, ...customAiGroups]
    return all
      .map(g => g.enrichment
        ? { ...g, fields: g.fields.filter(f => upstreamEnrichmentFields.includes(f.key)) }
        : g)
      .filter(g => g.fields.length > 0)
    // Keyed on serialized fields — the array's identity changes every render.
  }, [JSON.stringify(upstreamEnrichmentFields), customAiGroups.length])

  const availableKeys = useMemo(
    () => new Set(visibleGroups.flatMap(g => g.fields.map(f => f.key))),
    [visibleGroups],
  )

  // Drop selections that are no longer available (e.g. the upstream Enrich
  // Contacts node was removed, or a template default selected enrichment
  // fields nothing produces) so the saved config matches what can run.
  useEffect(() => {
    const pruned = selectedFields.filter(f => availableKeys.has(f))
    if (pruned.length !== selectedFields.length) onChange(pruned)
  }, [availableKeys, selectedFields])

  const toggle = (field: ContactContextField) => {
    if (selectedFields.includes(field)) {
      onChange(selectedFields.filter(f => f !== field))
    } else {
      onChange([...selectedFields, field])
    }
  }

  const selectAll = () => {
    const all = visibleGroups.flatMap(g => g.fields.map(f => f.key))
    onChange(all)
  }

  const clearAll = () => onChange([])

  // Names the field list for AT. Generated, not hardcoded — this selector is
  // mounted by more than one config panel and can appear twice on a screen.
  const fieldsLabelId = React.useId()

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span id={fieldsLabelId} className={labelClass} style={labelStyle}>
          Contact Fields for Personalization
        </span>
        <span className="text-muted-foreground dark:text-[#8a8f98]" style={labelStyle}>{selectedFields.length} selected</span>
      </div>
      <div className="flex gap-2 text-xs">
        <button type="button" onClick={selectAll} className="text-[#5e6ad2] dark:text-[#a5b4fc] hover:opacity-80 transition-opacity duration-150 font-medium">Select all</button>
        <span className="text-border/70">|</span>
        <button type="button" onClick={clearAll} className="text-muted-foreground hover:text-foreground dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] transition-colors duration-150 font-medium">Clear</button>
      </div>
      <div
        className="max-h-[360px] overflow-y-auto border border-border/60 dark:border-white/[0.08] rounded-md"
        role="group"
        aria-labelledby={fieldsLabelId}
      >
        {visibleGroups.map((group) => (
          <div key={group.name}>
            <div className="sticky top-0 z-10 px-3 py-1.5 bg-muted/95 dark:bg-[#1a1b1e]/95 backdrop-blur-sm border-b border-border/50 dark:border-white/[0.06]">
              <p className={sectionEyebrowClass} style={sectionEyebrowStyle}>{group.name}</p>
            </div>
            {group.fields.map((field) => {
              const isSelected = selectedFields.includes(field.key)
              return (
                <button
                  key={field.key}
                  type="button"
                  onClick={() => toggle(field.key)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors duration-150 border-b border-border/30 dark:border-white/[0.04] last:border-0 ${
                    isSelected ? 'bg-[#5e6ad2]/[0.08] dark:bg-[#5e6ad2]/[0.12]' : 'hover:bg-muted/60 dark:hover:bg-white/[0.03]'
                  }`}
                >
                  <div className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 transition-colors duration-150 ${
                    isSelected ? 'bg-[#5e6ad2] text-white' : 'border border-border/60 dark:border-white/[0.14]'
                  }`}>
                    {isSelected && <Check className="w-2.5 h-2.5" weight="bold" />}
                  </div>
                  <span className="flex items-center gap-1 text-[13px] text-foreground dark:text-[#f7f8f8]">
                    {field.label}
                    {upstreamEnrichmentFields.includes(field.key) && (
                      <span className="text-[9px] text-cyan-600 dark:text-cyan-400 font-medium">✨ enriched</span>
                    )}
                  </span>
                </button>
              )
            })}
          </div>
        ))}
      </div>
      {upstreamEnrichmentFields.length === 0 && (
        <p className="text-xs leading-relaxed text-muted-foreground dark:text-[#8a8f98]">
          Enrichment fields are hidden because no Enrich Contacts step runs before
          this node. Add one upstream to unlock AI and provider enrichment fields.
        </p>
      )}
    </div>
  )
}

export default ContactFieldSelector
